import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2, ArrowLeft, Upload, Camera, UserRound, Check, Plus } from 'lucide-react';
import { api, fileUrl } from '../lib/api';
import { downscaleToDataUrl } from '../lib/img';
import { toast } from '../hooks/use-toast';
import ComicRestore from './ComicRestore';
import QuickPersonCreate from './QuickPersonCreate';

// Grouped by occasion rather than by technique, because the person who bought
// this sells sunglasses and does not know what a framing modifier is. "The
// Amazon photo" and "the LinkedIn one" are the words they already use.
const NEEDS_LABEL = {
  product: 'your product photo',
  face: 'one of your people',
  both: 'a product and a person',
  none: 'nothing',
};

// What the frames of a session differ by, said plainly on the card. This is the
// difference between a session and eight rolls of the same dice, so it earns
// its line.
const AXIS_HINT = {
  'model-cap': 'one cap, every colourway',
  'model-sunglasses': 'one frame, every colourway',
  'model-tshirt': 'one garment, every colourway',
  'model-lookbook': 'one look, every colourway',
  'amazon-main': 'every angle a listing needs',
  'product-white': 'every angle a listing needs',
  'product-detail': 'every angle a listing needs',
  'linkedin-headshot': 'one face, ten usable versions',
  'corporate-portrait': 'one face, eight usable versions',
  'founder-portrait': 'one face, eight usable versions',
  'dating-profile': 'a profile’s worth in one sitting',
  'instagram-travel': 'a week of posts from one sitting',
  'instagram-lifestyle': 'a week of posts from one sitting',
};

// Este catalogo esta escrito en ingles britanico y se vende a un mercado que
// teclea en americano. "color" no esta dentro de "colour", asi que la sesion de
// colorizar no aparecia al buscarla por su propio nombre -- y quien la encargo
// pidio hoy que se anadiera, teniendola delante desde hacia semanas.
//
// Se normalizan los dos lados, lo escrito y lo buscado, de modo que las cien
// sesiones quedan cubiertas de una vez en lugar de las que uno se acuerde de
// etiquetar. Los acentos caen por lo mismo: aqui se busca en varios idiomas.
const norm = (s) => (s || '')
  .toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/colour/g, 'color')
  .replace(/grey/g, 'gray')
  .replace(/isation\b/g, 'ization')
  .replace(/ise\b/g, 'ize')
  .replace(/ising\b/g, 'izing')
  .replace(/ised\b/g, 'ized');

export default function PhotoRecipes({ onClose }) {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [pillars, setPillars] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');

  const [pick, setPick] = useState(null);
  const [comicOpen, setComicOpen] = useState(false);
  const [assetId, setAssetId] = useState('');
  const [influencerId, setInfluencerId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [starting, setStarting] = useState(false);
  // Inline "new person" form inside the Whose-face picker. Opens closed when
  // there are saved people, open when there are none -- with zero people the
  // old copy told the user to leave the modal, create one elsewhere and start
  // over, which is two context breaks in the flagship flow.
  const [personFormOpen, setPersonFormOpen] = useState(false);

  useEffect(() => {
    Promise.all([api.listPhotoRecipes(), api.listAssets(), api.listInfluencers()])
      .then(([r, a, p]) => {
        setPillars(r?.pillars || []);
        setRecipes(r?.recipes || []);
        setAssets((a || []).filter((x) => x.type !== 'style'));
        setPeople(p || []);
      })
      .catch((e) => toast({ title: 'Could not load the sessions', description: e.message }))
      .finally(() => setLoading(false));
  }, []);

  // Nombre, descripcion, pilar y lo que hay que traer: las cuatro formas en que
  // alguien recuerda una sesion. Escribir gana a la pestana, porque quien busca
  // "amazon" quiere las de Amazon y no las de Amazon dentro de donde estaba.
  const pillarLabel = useMemo(() => {
    const m = {};
    pillars.forEach((p) => { m[p.key] = p.label; });
    return m;
  }, [pillars]);

  const shown = useMemo(() => {
    const needle = norm(q.trim());
    const base = (!needle && tab !== 'all')
      ? recipes.filter((r) => r.pillar === tab)
      : recipes;
    if (!needle) return base;
    const words = needle.split(/\s+/);
    // Se busca por trozo y no por palabra entera, que es lo que hace que
    // "color" encuentre "colorize". El precio es que "old" tambien esta dentro
    // de "holding", asi que buscar "old photo" sacaba "Someone holding it" por
    // delante de "Restore an old photo". Se paga ordenando en vez de recortando:
    // primero lo que encaja en el nombre, luego en las palabras de busqueda, y
    // al final lo que solo coincide de pasada en la descripcion.
    const rank = (r) => {
      const label = norm(r.label || '');
      if (words.every((w) => label.includes(w))) return 0;
      // `aliases` no se ve en ninguna ficha: existe para encontrar una sesion
      // por lo que alguien quiere hacer -- "unblur", "fix", "damaged" -- y no
      // por como la hemos llamado nosotros.
      if (words.every((w) => norm(r.aliases || '').includes(w))) return 1;
      return 2;
    };
    return base
      .filter((r) => {
        const hay = norm(`${r.label || ''} ${r.blurb || ''} ${pillarLabel[r.pillar] || ''} `
                         + `${r.input_hint || ''} ${r.aliases || ''}`);
        return words.every((w) => hay.includes(w));
      })
      .map((r, i) => [rank(r), i, r])
      .sort((a, b) => a[0] - b[0] || a[1] - b[1])
      .map(([, , r]) => r);
  }, [recipes, tab, q, pillarLabel]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await downscaleToDataUrl(file, 1280);
      const a = await api.createAsset({
        type: 'product', name: file.name.replace(/\.[^.]+$/, '').slice(0, 60), dataUrl,
      });
      const withUrl = { ...a, url: a.url || (a.file_key ? `/api/files/${a.file_key}` : null) };
      setAssets((prev) => [withUrl, ...prev]);
      setAssetId(a.id);
    } catch (e) {
      toast({ title: 'Could not upload that image', description: e.message });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const needsProduct = pick && (pick.needs === 'product' || pick.needs === 'both');
  const needsFace = pick && (pick.needs === 'face' || pick.needs === 'both');
  const ready = !pick ? false : (!needsFace || !!influencerId);

  const start = async () => {
    setStarting(true);
    try {
      const r = await api.startPhotoRecipe({
        slug: pick.slug,
        assetId: needsProduct ? (assetId || null) : null,
        influencerId: needsFace ? (influencerId || null) : null,
      });
      // Close before navigating: when the target route is another studio
      // session, React Router reuses the component and this modal would
      // otherwise stay open, spinner frozen, over the new session.
      onClose?.();
      navigate(`/app/studio/${r.workflowId}`);
    } catch (e) {
      toast({ title: 'Could not start that session', description: e.message });
      setStarting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
         onClick={onClose}>
      <div className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-[#2A2A30] bg-[#0E0E10]"
           onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center gap-3 border-b border-[#2A2A30] px-5 py-4">
          {pick && (
            <button onClick={() => setPick(null)} className="text-white/50 hover:text-white">
              <ArrowLeft size={18} />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-white">
              {pick ? pick.label : 'Photo sessions'}
            </h2>
            <p className="truncate text-xs text-white/45">
              {pick ? pick.blurb : 'Pick what you need a photograph of. Everything else is already set.'}
            </p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center py-20">
            <Loader2 className="animate-spin text-white/40" />
          </div>
        ) : !pick ? (
          <>
            <div className="flex flex-col gap-2.5 border-b border-[#2A2A30] px-5 py-3">
              <div className="relative">
                <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  autoFocus
                  data-testid="recipe-search"
                  placeholder="Search sessions - restore, amazon, headshot, mug..."
                  className="h-9 w-full rounded-full border border-[#2A2A30] bg-[#0E0E10] pl-9 pr-9 text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none"
                />
                {q && (
                  <button onClick={() => setQ('')} title="Clear"
                          data-testid="recipe-search-clear"
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/35 hover:text-white">
                    <X size={13} />
                  </button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {/* Search looks across the whole catalogue no matter the tab, so
                    while typing the pillars are shown deselected rather than
                    disabled: clicking one clears the search and jumps straight
                    to that group instead of dead-ending in a greyed-out row. */}
                {[{ key: 'all', label: 'All' }, ...pillars].map((p) => (
                  <button key={p.key} onClick={() => { setQ(''); setTab(p.key); }}
                          className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
                            tab === p.key && !q.trim() ? 'bg-white text-black' : 'border border-[#2A2A30] text-white/60 hover:text-white'}`}>
                    {p.label}
                  </button>
                ))}
                <span className="ml-auto text-[11px] text-white/35">
                  {shown.length} of {recipes.length}
                </span>
              </div>
            </div>
            <div className="grid flex-1 gap-3 overflow-y-auto p-5 sm:grid-cols-2">
              {/* Local OpenCV, not a session: no canvas workflow, no credits.
                  Lives with the 'fix' restorations because that is where
                  somebody with an old scan looks. */}
              {(() => {
                const needle = norm(q.trim());
                const hay = norm('comic restoration restore modernize modernise book page scan '
                                 + 'lettering balloons old vintage fix colorize tebeo comic');
                const show = needle
                  ? needle.split(/\s+/).every((wd) => hay.includes(wd))
                  : (tab === 'all' || tab === 'fix');
                return show && (
                  <button onClick={() => setComicOpen(true)}
                          data-testid="recipe-comic-restore"
                          className="rounded-xl border border-emerald-400/30 bg-[#141417] p-4 text-left transition-colors hover:border-emerald-400/60">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-white">Comic restoration</p>
                      <span className="shrink-0 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[10px] text-emerald-300">
                        No credits · instant
                      </span>
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-white/45">
                      Clean a scanned comic page or give it flat modern colour. The lettering
                      is detected and kept exactly as printed.
                    </p>
                    <p className="mt-2 text-[11px] text-white/30">
                      You bring: a scanned page · runs on this machine, not an AI model
                    </p>
                  </button>
                );
              })()}
              {shown.length === 0 && (
                <div className="col-span-full py-16 text-center" data-testid="recipe-search-empty">
                  <p className="text-sm text-white/55">Nothing matches &ldquo;{q.trim()}&rdquo;.</p>
                  <button onClick={() => setQ('')}
                          className="mt-3 rounded-full border border-[#2A2A30] px-4 py-1.5 text-xs text-white/70 hover:border-white/40 hover:text-white">
                    Show all {recipes.length} sessions
                  </button>
                </div>
              )}
              {shown.map((r) => (
                <button key={r.slug} onClick={() => { setPick(r); setAssetId(''); setInfluencerId(''); setPersonFormOpen(false); }}
                        data-testid={`recipe-${r.slug}`}
                        className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4 text-left transition-colors hover:border-white/30">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-white">{r.label}</p>
                    <span className="shrink-0 rounded-full border border-[#2A2A30] px-2 py-0.5 text-[10px] text-white/50">
                      {r.shots} shots
                    </span>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-white/45">{r.blurb}</p>
                  {AXIS_HINT[r.slug] && (
                    <p className="mt-2 text-[11px] text-emerald-400/80">{AXIS_HINT[r.slug]}</p>
                  )}
                  <p className="mt-2 text-[11px] text-white/30">
                    You bring: {NEEDS_LABEL[r.needs] || 'nothing'} · {r.aspect}
                  </p>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto p-5">
            {needsFace && (
              <div>
                <p className="mb-2 text-xs uppercase tracking-wider text-white/40">
                  Whose face <span className="text-white/25">· required</span>
                </p>
                {people.length === 0 ? (
                  <div>
                    <p className="mb-2 text-xs text-white/45">
                      Save a face once, reuse it in every session.
                    </p>
                    <QuickPersonCreate
                      onCreated={(infl) => {
                        setPeople((prev) => [infl, ...prev]);
                        setInfluencerId(infl.id);
                        setPersonFormOpen(false);
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-2">
                      {people.map((p) => (
                        <button key={p.id} onClick={() => setInfluencerId(p.id)}
                                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                                  influencerId === p.id
                                    ? 'border-[#F5A623] bg-[#F5A623]/10 text-white'
                                    : 'border-[#2A2A30] bg-[#141417] text-white/70 hover:text-white'}`}>
                          {p.cover_url
                            ? <img src={fileUrl(p.cover_url)} alt="" className="h-6 w-6 rounded-full object-cover" />
                            : <UserRound size={14} />}
                          {p.name}
                          {influencerId === p.id && <Check size={13} />}
                        </button>
                      ))}
                      {!personFormOpen && (
                        <button onClick={() => setPersonFormOpen(true)}
                                data-testid="quick-person-open"
                                className="flex items-center gap-2 rounded-lg border border-dashed border-[#2A2A30] px-3 py-2 text-sm text-white/50 transition-colors hover:border-white/40 hover:text-white">
                          <Plus size={14} /> New person
                        </button>
                      )}
                    </div>
                    {personFormOpen && (
                      <div className="mt-2">
                        <QuickPersonCreate
                          onCancel={() => setPersonFormOpen(false)}
                          onCreated={(infl) => {
                            setPeople((prev) => [infl, ...prev]);
                            setInfluencerId(infl.id);
                            setPersonFormOpen(false);
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {needsProduct && (
              <div>
                <p className="mb-2 text-xs uppercase tracking-wider text-white/40">
                  What you sell <span className="text-white/25">· {pick.input_hint}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => fileRef.current?.click()} disabled={uploading}
                          className="flex h-16 w-16 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#2A2A30] text-white/40 hover:border-white/40 hover:text-white disabled:opacity-50">
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    <span className="text-[10px]">Upload</span>
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden"
                         onChange={(e) => upload(e.target.files?.[0])} />
                  {/* La seleccionada va en naranja con halo, no en blanco. Sobre
                      una rejilla de miniaturas en un panel oscuro, un borde
                      blanco fino es del mismo color que medio catalogo y no se
                      distingue de un simple hover. */}
                  {assets.slice(0, 23).map((a) => (
                    <button key={a.id} onClick={() => setAssetId(a.id === assetId ? '' : a.id)}
                            title={a.name}
                            className={`h-16 w-16 overflow-hidden rounded-lg border-2 transition-all ${
                              assetId === a.id
                                ? 'border-[#F5A623] ring-2 ring-[#F5A623]/45'
                                : 'border-[#2A2A30] hover:border-white/40'}`}>
                      {a.url
                        ? <img src={fileUrl(a.url)} alt={a.name} className="h-full w-full object-cover" />
                        : <span className="text-[10px] text-white/40">{a.name?.slice(0, 8)}</span>}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-white/30">
                  Skip this and the session opens with a described stand-in you can drop a
                  photo onto later.
                </p>
              </div>
            )}

            <div className="rounded-lg border border-[#2A2A30] bg-[#141417] p-3 text-xs text-white/50">
              {pick.shots} frames · {pick.aspect} · {pick.quality}
              {pick.identity === 'lock' && ' · the face is held to the reference'}
              {pick.identity === 'soft' && ' · the same invented model across the set'}
            </div>

            <button onClick={start} disabled={!ready || starting}
                    data-testid="recipe-start"
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-white py-3 text-sm font-medium text-black hover:bg-white/90 disabled:opacity-40">
              {starting ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
              {needsFace && !influencerId ? 'Pick a person first' : `Open this session`}
            </button>
          </div>
        )}
      </div>
      {comicOpen && <ComicRestore onClose={() => setComicOpen(false)} />}
    </div>
  );
}
